// Lab analyzers (docs/plans/phase2-lab-connector.md §5–6): the device registry in Settings
// and the review queue in the lab. Sample numbers and values travel only in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  analyzerCredentialSchema,
  analyzerMessageSchema,
  analyzerQueueItemSchema,
  analyzerSchema,
  pageSchema,
  type AnalyzerDriverConfig,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export type AnalyzerQueueState = 'queued' | 'dismissed' | 'applied';

export const analyzerKeys = {
  all: ['lab-analyzers'] as const,
  list: ['lab-analyzers', 'list'] as const,
  messages: (id: string) => ['lab-analyzers', 'messages', id] as const,
  queue: (state: AnalyzerQueueState) => ['lab-analyzers', 'queue', state] as const,
};

export const analyzersQuery = queryOptions({
  queryKey: analyzerKeys.list,
  queryFn: () => api('GET', '/admin/lab-analyzers', { schema: z.array(analyzerSchema) }),
});

export const analyzerMessagesQuery = (id: string) =>
  queryOptions({
    queryKey: analyzerKeys.messages(id),
    queryFn: () =>
      api('GET', `/admin/lab-analyzers/${id}/messages`, { schema: z.array(analyzerMessageSchema) }),
  });

export const analyzerQueueQuery = (state: AnalyzerQueueState) =>
  queryOptions({
    queryKey: analyzerKeys.queue(state),
    queryFn: () =>
      api('GET', `/lab/analyzer-results?state=${state}&limit=100`, {
        schema: pageSchema(analyzerQueueItemSchema),
      }),
  });

export const addAnalyzer = (body: { name: string; driver: AnalyzerDriverConfig }) =>
  api('POST', '/admin/lab-analyzers', { body, schema: analyzerCredentialSchema });

export const updateAnalyzer = (
  id: string,
  body: { expectedVersion: number; name?: string; driver?: AnalyzerDriverConfig },
) => api('PATCH', `/admin/lab-analyzers/${id}`, { body, schema: analyzerSchema });

export const rotateAnalyzerCredential = (id: string) =>
  api('POST', `/admin/lab-analyzers/${id}/credential`, { schema: analyzerCredentialSchema });

export const retireAnalyzer = (id: string, reason: string) =>
  api('POST', `/admin/lab-analyzers/${id}/retire`, { body: { reason }, schema: analyzerSchema });

export const putAnalyzerTestCodes = (
  id: string,
  body: {
    expectedVersion: number;
    testCodes: { analyzerCode: string; analyteId: string; expectedUnit: string | null }[];
  },
) => api('PUT', `/admin/lab-analyzers/${id}/test-codes`, { body, schema: analyzerSchema });

export const applyAnalyzerResult = (
  id: string,
  body: { sampleNumber?: string; replaceEntered: boolean; acceptNotFinal: boolean },
) => api('POST', `/lab/analyzer-results/${id}/apply`, { body, schema: analyzerQueueItemSchema });

export const dismissAnalyzerResult = (id: string, reason: string) =>
  api('POST', `/lab/analyzer-results/${id}/dismiss`, { body: { reason } });
